import { Clock, X } from 'lucide-react'
import { Button } from 'tailwind-v4/components/button'
import { cn } from '@/lib/utils'

export interface TrialBannerProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  /** Days left in the trial, floor'd; 0 = ends today. Computed from
   *  `trial_ends_at` with date-fns. */
  daysRemaining: number
  /** Escalates styling as the trial nears its end. */
  tone?: 'info' | 'warning'
  /** Customers can't self-serve upgrade — this opens a conversation with
   *  the team (e.g. support chat) instead of a billing page. */
  onContactUs: () => void
  /** Dismiss the banner. The component is stateless — the host decides
   *  whether/how the dismissal persists. */
  onDismiss: () => void
  /** Overridable contact-action label. */
  contactLabel?: string
}

/**
 * Persistent, org-wide banner telling a trialling user how long they have
 * left, with a shortcut to talk to the team. Info-toned, flat, tokens only;
 * the host owns all external spacing (the component carries no margin) and
 * decides where to mount it (e.g. above the page outlet, below the top bar).
 */
export function TrialBanner({
  daysRemaining,
  tone = 'info',
  onContactUs,
  onDismiss,
  contactLabel = 'Talk to us',
  className,
  ...props
}: TrialBannerProps) {
  const dayLabel = daysRemaining === 1 ? 'day' : 'days'
  const daysCopy =
    daysRemaining === 0
      ? 'Your trial ends today'
      : `${daysRemaining} ${dayLabel} left in your trial`

  return (
    <div
      data-slot="trial-banner"
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3',
        tone === 'warning'
          ? 'border-warning/25 bg-warning/5'
          : 'border-info/25 bg-info/5',
        className
      )}
      {...props}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          aria-hidden
          className={cn(
            'inline-flex shrink-0 [&_svg]:size-4',
            tone === 'warning' ? 'text-warning' : 'text-info'
          )}
        >
          <Clock />
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-sm font-medium text-foreground">{daysCopy}</p>
          <p className="text-xs text-muted-foreground">
            Speak to our team to add a payment method before your trial ends.
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onContactUs}>
          {contactLabel}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          <X />
        </Button>
      </div>
    </div>
  )
}
